/* eslint-disable camelcase */
import * as React from "react"
import { Appearance, ViewStyle } from "react-native"
import { observer } from "mobx-react-lite"
import { Actionsheet, ActionsheetProps } from "../actionsheet/actionsheet"
import { FC, PropsWithChildren, useCallback, useMemo, useState } from "react"
import { FormControl } from "../form-control/form-control"
import { Button } from "../button/button"
import { LazyRender } from "../lazy-render/lazy-render"
import { delay } from "../../utils/delay"
import { color } from "../../theme"
import { Divider, Radio, View } from "native-base"
import { translate, TxKeyPath } from "../../i18n"
import { Text } from "../text/text"
import { ModelPicker } from "../model-picker/model-picker"
import { Country, Region } from "../../database"
import { Picker } from "../picker/picker"

export interface DateTimeFilterProps extends PropsWithChildren<any> {
  value?: {
    country_id?: string,
    region_id?: string,
    verified?: string
  },

  isDisabled?: boolean,

  isOpen?: boolean,
  onClose?: () => void,

  onValueChange?: (value: any) => any

  actionsheetProps?: ActionsheetProps,

  colorScheme?: "light" | "dark",
  actionSheetColorScheme?: "light" | "dark"
  clearable?: boolean,
}

const FORM_CONTROL: ViewStyle = {
  paddingHorizontal: 16,
  marginBottom: 8
}

const BUTTON_CONTAINER: ViewStyle = {
  flexDirection: "row",
  paddingBottom: 16,
  paddingHorizontal: 16,
}

const ACTIONSHEET: ViewStyle = {
  minHeight: 0,
  height: undefined
}

const BUTTON: ViewStyle = {
  flex: 1
}

const ActionsheetComponent: FC<{
  colorScheme?: "light" | "dark"
  defaultValue?: {
    country_id?: string,
    region_id?: string,
    time?: string,
    verified?: string
  },
  onValueChange: (value: any) => void
}> = ({
  colorScheme,
  defaultValue = {},
  onValueChange: onValueChangeProp,
}) => {
  const [ countryId, setCountryId ] = useState<string>(defaultValue.country_id)
  const [ regionId, setRegionId ] = useState<string>(defaultValue.region_id)
  const [ time, setTime ] = useState<string>(defaultValue.time)
  const [ verified, setVerified ] = useState<string>(defaultValue.verified)

  const onReset = useCallback(async () => {
    await delay(1)
    onValueChangeProp && onValueChangeProp({})
  }, [onValueChangeProp])

  const onApply = useCallback(async () => {
    await delay(1)
    onValueChangeProp && onValueChangeProp(JSON.parse(JSON.stringify({
      country_id: countryId || undefined,
      region_id: regionId || undefined,
      time: time || undefined,
      verified: verified || undefined
    })))
  }, [countryId, onValueChangeProp, regionId, time, verified])

  const timeData = useMemo(() => ([
    { id: 'now', name: translate('shrimpPriceFilter.now') },
    { id: 'week', name: translate('shrimpPriceFilter.week') },
    { id: 'month', name: translate('shrimpPriceFilter.month') },
  ]), [])

  return (
    <>
      <FormControl
        labelTx={"shrimpPriceFilter.country"}
        colorScheme={colorScheme}
        labelColor={color.palette.gray800}
        style={FORM_CONTROL}
        // isRequired
      >
        <ModelPicker
          colorScheme={colorScheme}
          value={countryId || 'ID'}
          onValueChange={async (value) => {
            await setRegionId(null)
            await setCountryId(value)
          }}
          isUseSearch
          Model={Country}
          titleTx="farmForm.countryIdPlaceholder"
        />
      </FormControl>
      <FormControl
        labelTx={"shrimpPriceFilter.region"}
        colorScheme={colorScheme}
        labelColor={color.palette.gray800}
        style={FORM_CONTROL}
        // isRequired
      >
        <ModelPicker
          colorScheme={colorScheme}
          value={regionId}
          onValueChange={setRegionId}
          isUseSearch
          fetchData={(model, search) => (
            model.fetch({
              country_id__eq: countryId || 'ID',
              scope: 'regency',
              search,
              sort: 'full_name',
            })
          )}
          Model={Region}
          dataLabel="full_name"
          titleTx="shrimpPriceFilter.regionLabel"
        />
      </FormControl>
      <FormControl
        labelTx="shrimpPriceFilter.time"
        colorScheme={colorScheme}
        labelColor={color.palette.gray800}
        style={FORM_CONTROL}
        // isRequired
      >
        <Picker
          colorScheme={colorScheme}
          value={time || 'now'}
          data={timeData}
          onValueChange={setTime}
          isUseSearch={false}
          titleTx="shrimpPriceFilter.timeLabel"
        />
      </FormControl>
      <FormControl
        labelTx={"shrimpPriceFilter.supplier"}
        colorScheme={colorScheme}
        labelColor={color.palette.gray800}
        style={FORM_CONTROL}
      >
        <Radio.Group
          colorScheme={colorScheme}
          name="time"
          accessibilityLabel={translate("shrimpPriceFilter.verified")}
          value={verified || ""}
          onChange={setVerified}
        >
          <View flexGrow={1} flexDirection="row" justifyContent="space-between">
            {["", "verified"].map((verified, index) => (
              <Radio key={`radio-verified-${index}`} mr="36px" value={verified}>
                <Text tx={(verified && `shrimpPriceFilter.${verified}` || `shrimpPriceFilter.all`) as TxKeyPath} ml="8px"
                      colorScheme={colorScheme} />
              </Radio>
            ))}
          </View>
        </Radio.Group>
      </FormControl>
      <View style={BUTTON_CONTAINER}>
        <Button
          testID="jala:id/reset-shrimp-price-filter-button"
          tx="shrimpPriceFilter.reset"
          preset="outline"
          colorScheme={colorScheme}
          style={BUTTON}
          onPress={onReset}
        />
        <View width="16px" />
        <Button
          testID="jala:id/apply-shrimp-price-filter-button"
          tx="shrimpPriceFilter.apply"
          colorScheme={colorScheme}
          style={BUTTON}
          onPress={onApply}
        />
      </View>
    </>
  );
}

/**
 * Describe your component here
 */
export const ShrimpPriceFilter = observer(function DateTimeFilter(props: DateTimeFilterProps) {
  const {
    value = {},
    isOpen, onClose,
    onValueChange: onValueChangeProp,
    actionsheetProps,
    colorScheme = Appearance.getColorScheme(),
    actionSheetColorScheme = colorScheme,
    clearable
  } = props

  const onValueChange: (value: any) => void = useCallback((value: any) => {
    onValueChangeProp && onValueChangeProp(value)
    onClose()
  }, [onClose, onValueChangeProp])

  return (
    <Actionsheet
      colorScheme={actionSheetColorScheme}
      isOpen={isOpen}
      onClose={onClose}
      clearable={clearable}
      actionsheetStyle={ACTIONSHEET}
      {...actionsheetProps}
    >
      <LazyRender>
        <Divider />
        <ActionsheetComponent
          colorScheme={colorScheme}
          defaultValue={value}
          onValueChange={onValueChange}
        />
      </LazyRender>
    </Actionsheet>
  )
})
